import { detect } from 'detect-browser';
import {
  getAllQuery,
} from '@/lib/utils';

type UserBrowser = ReturnType<typeof detect>;

export class Browser {
  readonly #userBrowser: UserBrowser;
  readonly #navigator: Navigator;
  readonly queryName = 'isWebview';

  constructor() {
    this.#userBrowser = detect();
    this.#navigator = navigator;
  }

  get name(): string {
    // when defined via detect-browser,iphone returns ios as browser name (can only be via browserstack),
    // so static now returns safari.
    // When working with a macbook, there is no such problem, the browser name returns as safari

    const fallbackName = this.#userBrowser?.name ?? 'unknown';

    return ({ ios: 'safari' } as Record<string, string>)[fallbackName] ?? fallbackName;
  }

  get version(): string {
    return this.#userBrowser?.version ?? '0.0.0';
  }

  get majorVersion(): string {
    return this.#userBrowser?.version?.split('.')[0] ?? 'unknown';
  }

  get os(): string {
    return this.#userBrowser?.os ?? 'unknown';
  }

  get type(): string {
    return this.#userBrowser?.type ?? 'unknown';
  }

  get fullInfo(): UserBrowser {
    return this.#userBrowser;
  }

  get language(): string {
    return this.#navigator.language.substring(0, 2);
  }

  get isSafari(): boolean {
    return this.name === 'safari';
  }

  get isMobileBrowser(): boolean {
    const os = this.os.toLowerCase();
    return os.includes('android') || os.includes('ios');
  }

  get isMobileWebView(): boolean {
    const queryParams = getAllQuery(window.location.search);
    return this.queryName in queryParams && queryParams.isWebview === 'true';
  }

  get environmentType(): 'ios' | 'android' | 'web' {
    const osName = this.os.toLocaleLowerCase();
    if (this.isMobileWebView) {
      if (osName.includes('ios')) {
        return 'ios';
      }
      if (osName.includes('android')) {
        return 'android';
      }
    }
    return 'web';
  }
}
